import { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { IconChartPie, IconCoin, IconHome, IconTrendingUp, IconUsers } from '@tabler/icons-react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { BrandLogo } from '~portal/components/BrandLogo';
import { ActivityIndicator, ActivityLabels, MdContent } from '~portal/components/chat/blocks';
import { ChatInput } from '~portal/components/chat/ChatInput';
import * as turnStyles from '~portal/components/chat/TurnGroup.css';
import { ColorSchemeToggle } from '~portal/components/ColorSchemeToggle';
import {
  AnchorLink,
  ArrowButton,
  ArrowButtonLink,
  ArrowMark,
  ButtonLink,
  IllustrativeBadge,
  KpiStrip,
  NavLinkLink,
  PageLoading,
  PaperLink,
  SectionCard,
  UnderlineTabs,
  UnstyledButtonLink,
} from '~portal/components/common';
import { bareList, ruledRow } from '~portal/components/common/common.css';
import * as navStyles from '~portal/components/GlobalNav/GlobalNav.css';
import { ThemeSamples } from './-style-preview/ThemeSamples';
import { ListingPrimitivesPreview } from './-style-preview-listing';

/**
 * Dev-only visual test bed for the Charlie design system: renders the full
 * markdown style-guide corpus plus the chat chrome (user bubble, input) so the
 * theme can be inspected in all three colour schemes (light / dark / blue,
 * switched from the toggle in the header) without auth or a backend.
 * Unreachable in production builds.
 */
export const Route = createFileRoute('/style-preview')({
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw redirect({ to: '/' });
  },
  component: StylePreview,
});

const corpus = `
# MarkDown Headings One

## MarkDown Heading Two

### MarkDown Heading Three

#### Heading Four

##### Heading Five

###### Heading Six

MarkDown Body Text - used for any large chunks of text received by the ChatBot. Growth in Tier 1 sports-betting customers across South America has been substantial between 2021 and 2025, although performance varies considerably by market and customer segment.

**MarkDown Bold Text** - used for emphatic text received by the ChatBot.

*MarkDown Italics - used for emphatic references received by the ChatBot.*

~~MarkDown Body Strikethrough~~

***MarkDown Bold and Italics - used for emphatic references received by the ChatBot.***

> MarkDown Block Quote Item

---

https://www.automaticlinks.com

[Text Link](https://example.com/external)

| Column Heading One | Column Heading Two | Column Heading Three |
| --- | --- | --- |
| Column Row One.One | Column Row One.Two | Column Row One.Three |
| Column Row Two.One | Column Row Two.Two | Column Row Two.Three |
| Column Row Three.One | Column Row Three.Two | Column Row Three.Three |

Tables render as an ag-Grid in ag-Grid's own stock theme, sitting directly in the prose flow with no card or header chrome — figures are right-aligned and sort by value, the alignment row wins where the author states one, and every column is resizable. The copy and download controls sit muted under the table. A short colon-terminated lead-in directly above a table is promoted to a heading and loses its colon. A header wraps rather than truncating, and a parenthetical stays whole on its own line.

Regulated markets by GGR, 2025:

| Market | Operators | GGR, 2025 (USD m) | Online share (%) |
| :--- | :---: | ---: | --- |
| United Kingdom | 42 | 1,204.5 | 38% |
| Malta | 9 | -3.2 | 2% |
| Brazil | 118 | 912.0 | 27% |

in-line code preamble \`const dashboard = {label: "Revenue", value: 4285000, change: 8.4 }\`

\`\`\`javascript
const dashboard = {
  title: "Market Overview",
  status: "active",
  metrics: [
    { label: "Revenue", value: 4285000, change: 8.4 },
    { label: "Users", value: 184200, change: 3.1 }
  ]
};

function formatValue(value) {
  return new Intl.NumberFormat("en-GB").format(value);
}
\`\`\`

### Markdown Bulleted Lists - Title

- Markdown Bulleted Lists - Bullet
- Markdown Bulleted Lists - Bullet
- Markdown Bulleted Lists - Bullet
  - Nested bullet item

### Markdown Numbered Lists - Title

1. Markdown Numbered Lists - One
2. Markdown Numbered Lists - Two
3. Markdown Numbered Lists - Three

### Markdown To-Do Lists - Title

- [ ] Markdown To-Do - Item
- [x] Markdown To-Do - Completed Item
- [ ] Markdown To-Do - Item

A sentence carrying a footnote reference.[^1]

[^1]: Footnotes align right under a horizontal rule in mdREF style.

## Inline artefact references

Charts and data files the agent produced are referenced from the prose with the \`artifact:\` scheme (H2-206). The ids below are deliberately fake, so this section renders the *unresolved* state of each — the explicit "Chart unavailable" marker that replaced an \`<img>\` with no src. Against a live backend the first becomes an inline chart and the second an inline download card.

![Europe channel trend, 2021-2026](artifact:000000000000000000000000)

The full series is in [the channel breakdown](artifact:000000000000000000000001).
`;

/**
 * Every state the in-flight activity line can be in, including the joined pair a two-activity
 * tool batch produces. Samples, not a sequence: the live line shows one of these at a time and
 * cross-fades between them.
 */
const ACTIVITY_SAMPLES: string[] = [
  ...Object.values(ActivityLabels),
  'Querying the data and building a chart',
];

const NAV_STATES = [
  { label: 'Idle', className: '', selected: false },
  { label: 'Hovered', className: navStyles.forcedHover, selected: false },
  { label: 'Pressed', className: navStyles.forcedPressed, selected: false },
  { label: 'Selected', className: '', selected: true },
] as const;

const PREVIEW_TABS = [
  { value: 'brand', label: 'By Brand' },
  { value: 'group', label: 'By Group' },
  { value: 'within', label: 'Within Group' },
] as const;

type PreviewTab = (typeof PREVIEW_TABS)[number]['value'];

/** Figma's KPI row (`433:9783`), with the portal's abbreviations rather than the full digits. */
const KPI_CARDS = [
  { label: 'GGR', value: '$18.9bn', icon: <IconCoin size={22} /> },
  { label: 'Monthly Active Users', value: '7.1m', icon: <IconUsers size={22} /> },
  { label: 'Market Share', value: '32%', icon: <IconChartPie size={22} /> },
  { label: 'Growth (YoY)', value: '4.8%', delta: 0.048, icon: <IconTrendingUp size={22} /> },
];

const KPI_STRIP = [
  { label: 'Revenue', value: '£5.3bn', delta: 0.05 },
  { label: 'Adjusted EBITDA', value: '£1.1bn', hint: '21.1% margin' },
  { label: 'Operating profit', value: '£420m', delta: -0.031 },
  { label: 'MAU', value: '7.1m', delta: 0 },
];

const SECTION_ROWS = [
  'Operator Information: Ladbrokes',
  'Regulatory update: Spain',
  'Analyst note',
];

/** The account block's two states, static — the live block needs auth and the account data. */
function AccountBlockSample({ selected }: { selected: boolean }) {
  return (
    <UnstyledButton
      aria-label={selected ? 'Account block, selected' : 'Account block, idle'}
      className={navStyles.accountButton}
      data-selected={selected || undefined}
      w={240}
    >
      <Group gap="sm" wrap="nowrap" p="xs">
        <Avatar name="Amelia Hart" color="initials" size={36} radius="xl" />
        <Stack gap={0} miw={0} flex={1}>
          <Text size="sm" fw={500} lh={1.3} className={navStyles.line}>
            Amelia Hart
          </Text>
          <Text size="xs" c="dimmed" lh={1.3} className={navStyles.line}>
            {selected ? 'Selected (on /account)' : 'Idle'}
          </Text>
        </Stack>
      </Group>
    </UnstyledButton>
  );
}

function StylePreview() {
  // One clock for every sample, started when the page was opened, so the counters tick as they
  // would in a real turn instead of sitting frozen at a made-up number.
  const [turnStart] = useState(() => new Date().toISOString());
  const [tab, setTab] = useState<PreviewTab>('brand');

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <Group gap="md">
            <BrandLogo height={28} />
            <Title order={3}>Charlie style preview</Title>
          </Group>
          <ColorSchemeToggle />
        </Group>

        <Box className={turnStyles.userPrompt} ml="auto">
          <Paper p="md" radius="lg" className={turnStyles.userBubble}>
            <Text size="sm">
              Compare the growth in customers for Tier 1 sports-betting across the South American
              market between 2021 and 2025 — we want to understand if increased value in outlay will
              outweigh the user-attrition in this specific market.
            </Text>
          </Paper>
        </Box>

        <Box className={turnStyles.markdownOutput}>
          <MdContent text={corpus} />
        </Box>

        <Divider />

        <Stack gap={0}>
          <Title order={4}>Activity indicator</Title>
          <Text size="sm" c="dimmed">
            What a user sees while a turn runs, in place of the developer tool trace.
          </Text>
          {ACTIVITY_SAMPLES.map((label) => (
            <ActivityIndicator key={label} label={label} startedAt={turnStart} />
          ))}
        </Stack>

        <Divider />

        <Stack gap={0}>
          <Title order={4}>Page loading</Title>
          <Text size="sm" c="dimmed">
            A page waiting on its data: nothing for 300 ms, then this, centred in the content area.
            Shown here with no delay.
          </Text>
          <PageLoading label="Loading market…" delayMs={0} minHeight={200} />
        </Stack>

        <Divider />

        <Stack gap="xs">
          <Title order={4}>Global nav</Title>
          <Text size="sm" c="dimmed">
            The four pill states of Figma component 12:780, forced on side by side — idle, hovered,
            pressed, selected. Compare in each scheme.
          </Text>
          <Group gap="md" wrap="wrap">
            {NAV_STATES.map(({ label, className, selected }) => (
              <Box
                key={label}
                component="span"
                className={`${navStyles.item} ${className}`}
                data-selected={selected || undefined}
                w={224}
              >
                <IconHome size={22} stroke={1.5} aria-hidden />
                {label}
              </Box>
            ))}
          </Group>
          <Text size="sm" c="dimmed">
            The account block, idle and selected (G7): the selected tint is the pills&apos; own.
          </Text>
          <Group gap="md" wrap="wrap">
            <AccountBlockSample selected={false} />
            <AccountBlockSample selected />
          </Group>
        </Stack>

        <Divider />

        <Stack gap="xs">
          <Title order={4}>Arrow button (G8)</Title>
          <Text size="sm" c="dimmed">
            Idle, hovered (forced), disabled, small; then the link form and the decorative mark
            inside a card that is itself the link (hover the card).
          </Text>
          <Group gap="md" wrap="wrap" align="center">
            <ArrowButton label="Idle" />
            <ArrowButton label="Hovered" data-preview-state="hover" />
            <ArrowButton label="Disabled" disabled />
            <ArrowButton label="Small" size="sm" />
            <ArrowButtonLink label="Link to home" to="/" />
            <PaperLink to="/" withBorder radius="lg" p="md" aria-label="Entain, card as a link">
              <Group gap="md" wrap="nowrap">
                <Box>
                  <Text fw={600}>Entain</Text>
                  <Text size="sm" c="dimmed">
                    Card that is one link
                  </Text>
                </Box>
                <ArrowMark />
              </Group>
            </PaperLink>
          </Group>
        </Stack>

        <Divider />

        <Stack gap="xs">
          <Title order={4}>Section card</Title>
          <Text size="sm" c="dimmed">
            Title (theme heading face), badge, description, action, ruled rows on a bare list, and
            the footer link.
          </Text>
          <SectionCard
            title="Recent updates"
            badge={<IllustrativeBadge />}
            description="What changed on the platform this week"
            action={
              <Button variant="default" size="xs">
                Action
              </Button>
            }
            footer={<AnchorLink to="/">See more</AnchorLink>}
          >
            <Stack component="ul" gap={0} className={bareList}>
              {SECTION_ROWS.map((row) => (
                <Group
                  key={row}
                  component="li"
                  justify="space-between"
                  py="sm"
                  className={ruledRow}
                >
                  <Text>{row}</Text>
                  <ArrowButtonLink label={`Open ${row}`} to="/" size="sm" />
                </Group>
              ))}
            </Stack>
          </SectionCard>
        </Stack>

        <Divider />

        <Stack gap="xs">
          <Title order={4}>Underline tabs (O3, R2, A2)</Title>
          <Text size="sm" c="dimmed">
            Equal widths across the content, 4px indicator on the track; arrow keys move and
            activate.
          </Text>
          <UnderlineTabs
            tabs={PREVIEW_TABS}
            value={tab}
            onChange={setTab}
            aria-label="Preview tabs"
          />
        </Stack>

        <Divider />

        <Stack gap="xs">
          <Title order={4}>KPI strip (C4, G6)</Title>
          <Text size="sm" c="dimmed">
            The strip variant (company pages) and the cards variant (mastheads); figures are sans
            with tabular numerals.
          </Text>
          <KpiStrip items={KPI_STRIP} caption="FY2025 · illustrative" />
          <KpiStrip variant="cards" items={KPI_CARDS} illustrative={false} />
        </Stack>

        <Divider />

        <Stack gap="xs">
          <Title order={4}>Router links</Title>
          <Text size="sm" c="dimmed">
            One typed primitive per Mantine host; all are anchors.
          </Text>
          <Group gap="md" wrap="wrap" align="center">
            <AnchorLink to="/">AnchorLink</AnchorLink>
            <ButtonLink to="/" variant="light">
              ButtonLink
            </ButtonLink>
            <UnstyledButtonLink to="/">UnstyledButtonLink</UnstyledButtonLink>
            <Box w={200}>
              <NavLinkLink to="/" label="NavLinkLink" leftSection={<IconHome size={16} />} />
            </Box>
          </Group>
        </Stack>

        <Divider />

        <ThemeSamples />

        <Divider />

        <ListingPrimitivesPreview />

        <Divider />

        <ChatInput onSubmit={() => undefined} />
      </Stack>
    </Container>
  );
}
